import { db } from '../firebaseConfig';
import { collection, query, where, getDocs } from 'firebase/firestore';

export class CIFGenerator {
  /**
   * Generate a unique CIF number for the specified client type
   * @param {string} clientType - 'individual', 'business', or 'trust'
   * @returns {Promise<string>} - The generated CIF number
   */
  static async generateCIF(clientType) {
    console.log('=== CIFGenerator.generateCIF called ===');
    console.log('Generating CIF for client type:', clientType);
    console.log('Client type type:', typeof clientType);
    
    const prefix = this.getPrefix(clientType);
    console.log('Using prefix:', prefix);
    console.log('Prefix type:', typeof prefix);
    
    const cifNumber = await this.generateUniqueNumber(prefix);
    console.log('Generated CIF number:', cifNumber);
    console.log('CIF number type:', typeof cifNumber);
    console.log('=== CIFGenerator.generateCIF completed ===');
    return cifNumber;
  }

  /**
   * Get the prefix for the client type
   * @param {string} clientType - 'individual', 'business', or 'trust'
   * @returns {string} - The prefix (1, 2, or 3)
   */
  static getPrefix(clientType) {
    console.log('=== getPrefix called ===');
    console.log('Client type received:', clientType);
    console.log('Client type type:', typeof clientType);
    
    switch (clientType) {
      case 'individual':
        console.log('Returning prefix for individual: 1');
        return '1';
      case 'business':
        console.log('Returning prefix for business: 2');
        return '2';
      case 'trust':
        console.log('Returning prefix for trust: 3');
        return '3';
      default:
        console.error('Invalid client type:', clientType);
        throw new Error('Invalid client type. Must be individual, business, or trust.');
    }
  }

  /**
   * Generate a unique number with the given prefix
   * @param {string} prefix - The prefix (1, 2, or 3)
   * @returns {Promise<string>} - The unique CIF number
   */
  static async generateUniqueNumber(prefix) {
    let attempts = 0;
    const maxAttempts = 100;

    while (attempts < maxAttempts) {
      // Generate a random 6-digit number
      const randomDigits = Math.floor(Math.random() * 900000) + 100000; // 100000 to 999999
      
      // Create the CIF number: prefix + 6 digits + 1
      const cifNumber = `${prefix}${randomDigits}1`;
      
      console.log(`Attempt ${attempts + 1}: Generated CIF ${cifNumber}`);
      
      try {
        // Check if this CIF number already exists
        const isUnique = await this.isCIFUnique(cifNumber);
        
        if (isUnique) {
          console.log(`CIF ${cifNumber} is unique, returning`);
          return cifNumber;
        } else {
          console.log(`CIF ${cifNumber} already exists, trying again`);
        }
      } catch (error) {
        console.error('Error checking uniqueness, using fallback:', error);
        // If there's an error checking uniqueness, use the generated number
        return cifNumber;
      }
      
      attempts++;
    }
    
    // If we can't generate a unique number, generate one without checking
    const fallbackDigits = Math.floor(Math.random() * 900000) + 100000;
    const fallbackCIF = `${prefix}${fallbackDigits}1`;
    console.log(`Using fallback CIF: ${fallbackCIF}`);
    return fallbackCIF;
  }

  /**
   * Check if a CIF number is unique in the database
   * @param {string} cifNumber - The CIF number to check
   * @returns {Promise<boolean>} - True if unique, false if exists
   */
  static async isCIFUnique(cifNumber) {
    try {
      console.log('Checking uniqueness for CIF:', cifNumber);
      const clientsRef = collection(db, 'clients');
      const q = query(clientsRef, where('cifNumber', '==', cifNumber));
      const querySnapshot = await getDocs(q);
      
      const isUnique = querySnapshot.empty;
      console.log(`CIF ${cifNumber} uniqueness check result:`, isUnique);
      return isUnique; // Returns true if no documents found (unique)
    } catch (error) {
      console.error('Error checking CIF uniqueness:', error);
      // If there's an error checking, assume it's unique to avoid blocking
      return true;
    }
  }

  /**
   * Validate a CIF number format
   * @param {string} cifNumber - The CIF number to validate
   * @returns {boolean} - True if valid format, false otherwise
   */
  static validateCIF(cifNumber) {
    // CIF should be 8 digits: prefix(1) + 6 digits + suffix(1)
    const cifRegex = /^[1-3]\d{6}1$/;
    return cifRegex.test(cifNumber);
  }

  /**
   * Get client type from CIF number
   * @param {string} cifNumber - The CIF number
   * @returns {string} - The client type ('individual', 'business', 'trust')
   */
  static getClientTypeFromCIF(cifNumber) {
    if (!this.validateCIF(cifNumber)) {
      throw new Error('Invalid CIF number format');
    }

    const prefix = cifNumber.charAt(0);
    switch (prefix) {
      case '1':
        return 'individual';
      case '2':
        return 'business';
      case '3':
        return 'trust';
      default:
        throw new Error('Invalid CIF prefix');
    }
  }

  /**
   * Test function to generate a CIF without database check
   * @param {string} clientType - 'individual', 'business', or 'trust'
   * @returns {string} - A generated CIF number
   */
  static generateTestCIF(clientType) {
    const prefix = this.getPrefix(clientType);
    const randomDigits = Math.floor(Math.random() * 900000) + 100000;
    const cifNumber = `${prefix}${randomDigits}1`;
    console.log('Test CIF generated:', cifNumber);
    return cifNumber;
  }
} 